// @ts-nocheck
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger, SheetBody, SheetFooter } from './ui/sheet';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { ScrollArea } from './ui/scroll-area';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { AdvancedDataTableV2, ColumnDef, BulkAction } from './advanced-data-table-v2';
import { 
  Plus, 
  Lightbulb, 
  ExternalLink,
  Eye,
  Edit,
  MessageSquare,
  DollarSign,
  Calendar,
  MoreHorizontal,
  Archive,
  Trash2,
  Tag as TagIcon,
  Users,
  Building,
  Loader2,
  Layers,
  ChevronsUpDown,
  Check,
  X,
  Merge,
  Upload
} from 'lucide-react';
import { supabase, type Idea } from '@product-radar/shared';
import { useWorkspace, useRegionalSettings } from '@/contexts/context';
import { hasPermission, type UserRole } from '@product-radar/shared';
import { ProductNodeSelector } from './product-node-selector';
import { ClientSelector } from './client-selector';
import { formatCurrency, formatDate } from '@/lib/formatting';
import { MergeIdeasDialog } from './merge-ideas-dialog';
import { ImportIdeasDialog } from './import-ideas-dialog';

interface IdeaItem {
  id: string;
  title: string;
  description: string;
  linkedFeedback: number;
  linkedClients: number;
  effort: 1 | 2 | 3 | 4 | 5;
  clientValue: number;
  impact: 1 | 2 | 3 | 4 | 5;
  reach?: number | null;
  confidence?: number | null;
  time_criticality?: number | null;
  risk_reduction?: number | null;
  status: string;
  priority: 1 | 2 | 3 | 4 | 5;
  tags: string[];
  jiraLinks?: Array<{ jira_key: string; jira_url?: string; jira_status?: string }>;
  jiraLink?: string;
  jiraStatus?: string;
  createdAt: string;
  productAreas?: Array<{
    id: string;
    name: string;
    type: string;
  }>;
}

interface IdeaManagementProps {
  onViewDetails: (id: string) => void;
}

export function IdeaManagement({ onViewDetails }: IdeaManagementProps) {
  const { workspace, membership } = useWorkspace();
  const userRole = membership?.role as UserRole;
  const regionalSettings = useRegionalSettings();
  const [ideas, setIdeas] = useState<IdeaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingIdea, setEditingIdea] = useState<IdeaItem | null>(null);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeSelectedIds, setMergeSelectedIds] = useState<string[]>([]);

  // Form state for new idea
  const [newIdea, setNewIdea] = useState({
    title: '',
    description: '',
    status: 'New',
    priority: 3 as 1 | 2 | 3 | 4 | 5,
    effort: 3 as 1 | 2 | 3 | 4 | 5,
    impact: 3 as 1 | 2 | 3 | 4 | 5,
    reach: '' as string,
    confidence: null as number | null,
    time_criticality: null as number | null,
    risk_reduction: null as number | null
  });
  const [selectedProductNodes, setSelectedProductNodes] = useState<string[]>([]);
  const [selectedClients, setSelectedClients] = useState<string[]>([]);

  // Form state for editing idea
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    status: 'New',
    priority: 3 as 1 | 2 | 3 | 4 | 5,
    effort: 3 as 1 | 2 | 3 | 4 | 5,
    impact: 3 as 1 | 2 | 3 | 4 | 5,
    reach: '' as string,
    confidence: null as number | null,
    time_criticality: null as number | null,
    risk_reduction: null as number | null
  });
  const [editSelectedClients, setEditSelectedClients] = useState<string[]>([]);

  useEffect(() => {
    if (workspace) {
      loadIdeas();
    }
  }, [workspace]);

  const loadIdeas = async () => {
    if (!workspace) return;
    
    setLoading(true);
    try {
      // Load ideas with related counts (filtered by workspace)
      const { data: ideasData, error: ideasError } = await supabase
        .from('ideas')
        .select(`
          *,
          idea_tags(tag:tags(name)),
          feedback_ideas!feedback_ideas_idea_id_fkey(
            feedback_id,
            feedback:feedback(
              id,
              client_id,
              client:clients(id, name, arr)
            )
          ),
          idea_product_mapping(
            product_item_id,
            product_id,
            product_item:product_structure_items(id, name, type),
            product:products(id, name, color)
          ),
          idea_jira_links(jira_key, jira_url, jira_status)
        `)
        .eq('workspace_id', workspace.id)
        .order('created_at', { ascending: false });

      if (ideasError) throw ideasError;

      // Transform data
      const transformedIdeas: IdeaItem[] = (ideasData || []).map((item: any) => {
        // Get unique clients from linked feedback
        const clientsMap = new Map();
        let totalValue = 0;
        
        if (Array.isArray(item.feedback_ideas)) {
          item.feedback_ideas.forEach((fi: any) => {
            if (fi.feedback?.client) {
              const client = fi.feedback.client;
              if (!clientsMap.has(client.id)) {
                clientsMap.set(client.id, client);
                // Add client's ARR to total value
                if (client.arr) {
                  totalValue += parseFloat(client.arr) || 0;
                }
              }
            }
          });
        }

        return {
          id: item.id,
          title: item.title,
          description: item.description,
          linkedFeedback: Array.isArray(item.feedback_ideas) ? item.feedback_ideas.length : 0,
          linkedClients: clientsMap.size,
          effort: item.effort,
          clientValue: totalValue,
          impact: item.impact,
          reach: item.reach,
          confidence: item.confidence,
          time_criticality: item.time_criticality,
          risk_reduction: item.risk_reduction,
          status: item.status,
          priority: item.priority,
          tags: item.idea_tags?.map((it: any) => it.tag?.name).filter(Boolean) || [],
          jiraLinks: item.idea_jira_links ?? [],
          jiraLink: (item.idea_jira_links?.[0] as any)?.jira_url ?? item.jira_link,
          jiraStatus: (item.idea_jira_links?.[0] as any)?.jira_status ?? item.jira_status,
          createdAt: item.created_at,
          productAreas: [
            ...(item.idea_product_mapping?.filter((m: any) => m.product_item).map((m: any) => m.product_item) || []),
            ...(item.idea_product_mapping?.filter((m: any) => m.product).map((m: any) => ({ ...m.product, type: 'product' })) || [])
          ],
          productAreaNames: [
            ...(item.idea_product_mapping?.filter((m: any) => m.product_item).map((m: any) => m.product_item?.name) || []),
            ...(item.idea_product_mapping?.filter((m: any) => m.product).map((m: any) => m.product?.name) || [])
          ].filter(Boolean).join(', ')
        };
      });

      setIdeas(transformedIdeas);
    } catch (error: any) {
      console.error('Error loading ideas:', error);
      toast.error('Failed to load ideas', {
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateIdea = async () => {
    if (!workspace) {
      toast.error('Workspace not loaded');
      return;
    }
    
    if (!newIdea.title || !newIdea.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      // Create the idea
      const { data, error } = await supabase
        .from('ideas')
        .insert({
          workspace_id: workspace.id,
          title: newIdea.title,
          description: newIdea.description,
          status: newIdea.status,
          priority: newIdea.priority,
          effort: newIdea.effort,
          impact: newIdea.impact,
          reach: newIdea.reach ? parseInt(newIdea.reach, 10) : null,
          confidence: newIdea.confidence,
          time_criticality: newIdea.time_criticality,
          risk_reduction: newIdea.risk_reduction,
          created_by: membership?.user_id
        })
        .select()
        .single();

      if (error) throw error;

      // Link to product nodes if any selected
      if (selectedProductNodes.length > 0) {
        const productLinks = selectedProductNodes.map(nodeId => ({
          idea_id: data.id,
          product_item_id: nodeId,
          mapped_at: new Date().toISOString(),
          mapped_by: membership?.user_id
        }));

        const { error: linkError } = await supabase
          .from('idea_product_mapping')
          .insert(productLinks);

        if (linkError) {
          console.error('Error linking product nodes:', linkError);
          toast.error('Idea created but failed to link product areas', {
            description: linkError.message
          });
        }
      }

      // Link to clients if any selected
      if (selectedClients.length > 0) {
        const clientLinks = selectedClients.map(clientId => ({
          idea_id: data.id,
          client_id: clientId,
          linked_at: new Date().toISOString()
        }));

        const { error: clientLinkError } = await supabase
          .from('client_ideas')
          .insert(clientLinks);

        if (clientLinkError) {
          console.error('Error linking clients:', clientLinkError);
          toast.error('Idea created but failed to link clients', {
            description: clientLinkError.message
          });
        }
      }

      toast.success('Idea created successfully!');
      setDialogOpen(false);
      setNewIdea({
        title: '',
        description: '',
        status: 'New',
        priority: 3,
        effort: 3,
        impact: 3,
        reach: '',
        confidence: null,
        time_criticality: null,
        risk_reduction: null
      });
      setSelectedProductNodes([]);
      setSelectedClients([]);
      
      loadIdeas();
    } catch (error: any) {
      console.error('Error creating idea:', error);
      toast.error('Failed to create idea', {
        description: error.message
      });
    }
  };

  const handleEditIdea = async (idea: IdeaItem) => {
    setEditingIdea(idea);
    setEditForm({
      title: idea.title,
      description: idea.description,
      status: idea.status,
      priority: idea.priority,
      effort: idea.effort,
      impact: idea.impact,
      reach: idea.reach != null ? String(idea.reach) : '',
      confidence: idea.confidence ?? null,
      time_criticality: idea.time_criticality ?? null,
      risk_reduction: idea.risk_reduction ?? null
    });
    setEditDialogOpen(true);
    
    // Load existing client links
    try {
      const { data, error } = await supabase
        .from('client_ideas')
        .select('client_id')
        .eq('idea_id', idea.id);
      
      if (error) throw error;
      setEditSelectedClients(data?.map(link => link.client_id) || []);
    } catch (error: any) {
      console.error('Error loading client links:', error);
      setEditSelectedClients([]);
    }
  };

  const handleUpdateIdea = async () => {
    if (!editingIdea) return;

    try {
      // Update the idea
      const { error } = await supabase
        .from('ideas')
        .update({
          title: editForm.title,
          description: editForm.description,
          status: editForm.status,
          priority: editForm.priority,
          effort: editForm.effort,
          impact: editForm.impact,
          reach: editForm.reach ? parseInt(editForm.reach, 10) : null,
          confidence: editForm.confidence,
          time_criticality: editForm.time_criticality,
          risk_reduction: editForm.risk_reduction,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editingIdea.id);

      if (error) throw error;

      // Update client links - delete existing and add new ones
      // First, delete all existing links
      const { error: deleteError } = await supabase
        .from('client_ideas')
        .delete()
        .eq('idea_id', editingIdea.id);

      if (deleteError) {
        console.error('Error deleting existing client links:', deleteError);
      }

      // Then, add new links
      if (editSelectedClients.length > 0) {
        const clientLinks = editSelectedClients.map(clientId => ({
          idea_id: editingIdea.id,
          client_id: clientId,
          linked_at: new Date().toISOString()
        }));

        const { error: insertError } = await supabase
          .from('client_ideas')
          .insert(clientLinks);

        if (insertError) {
          console.error('Error inserting client links:', insertError);
          toast.error('Idea updated but failed to update client links', {
            description: insertError.message
          });
        }
      }

      toast.success('Idea updated successfully');
      setEditDialogOpen(false);
      setEditingIdea(null);
      loadIdeas();
    } catch (error: any) {
      console.error('Error updating idea:', error);
      toast.error('Failed to update idea', {
        description: error.message
      });
    }
  };

  const handleDeleteIdea = async (id: string) => {
    try {
      const { error } = await supabase
        .from('ideas')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Idea deleted successfully');
      loadIdeas();
    } catch (error: any) {
      console.error('Error deleting idea:', error);
      toast.error('Failed to delete idea', {
        description: error.message
      });
    }
  };

  const handleBulkAction = async (action: string, selectedIds: (string | number)[]) => {
    try {
      if (action === 'merge') {
        setMergeSelectedIds(selectedIds.map(String));
        setMergeDialogOpen(true);
        return;
      }
      if (action === 'delete') {
        const { error } = await supabase
          .from('ideas')
          .delete()
          .in('id', selectedIds);

        if (error) throw error;

        toast.success(`Deleted ${selectedIds.length} ideas`);
        loadIdeas();
      } else if (action === 'archive') {
        const { error } = await supabase
          .from('ideas')
          .update({ status: 'Archived' })
          .in('id', selectedIds);

        if (error) throw error;

        toast.success(`Archived ${selectedIds.length} ideas`);
        loadIdeas();
      } else {
        toast.success(`${action} applied to ${selectedIds.length} ideas`);
      }
    } catch (error: any) {
      console.error(`Error performing bulk action:`, error);
      toast.error(`Failed to ${action} ideas`, {
        description: error.message
      });
    }
  };

  const handleRefresh = () => {
    loadIdeas();
    toast.success('Ideas data refreshed!');
  };

  const handleExport = () => {
    const headers = ['Title', 'Status', 'Priority', 'Effort', 'Impact', 'Client Value', 'Linked Feedback', 'Created'];
    const csvContent = [
      headers.join(','),
      ...ideas.map(idea => [
        `"${idea.title.replace(/"/g, '""')}"`,
        idea.status,
        idea.priority,
        idea.effort,
        idea.impact,
        idea.clientValue,
        idea.linkedFeedback,
        new Date(idea.createdAt).toISOString()
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ideas-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();

    toast.success('Ideas data exported successfully!');
  };

  const getPriorityDot = (priority: number) => {
    const colors = {
      1: 'bg-gray-400',
      2: 'bg-blue-500',
      3: 'bg-yellow-500',
      4: 'bg-orange-500',
      5: 'bg-red-500'
    };
    return (
      <div className="flex items-center space-x-1">
        <div className={`h-2 w-2 rounded-full ${colors[priority as keyof typeof colors]}`} />
        <span className="text-sm">{priority}</span>
      </div>
    );
  };

  const getScoreDots = (score: number) => {
    const colors = {
      1: 'bg-gray-400',
      2: 'bg-blue-500',
      3: 'bg-yellow-500',
      4: 'bg-orange-500',
      5: 'bg-red-500'
    };
    return (
      <div className="flex items-center space-x-1">
        <div className={`h-2 w-2 rounded-full ${colors[score as keyof typeof colors]}`} />
        <span className="text-sm">{score}</span>
      </div>
    );
  };

  // Table configuration
  const columns: ColumnDef<IdeaItem>[] = [
    {
      key: 'title',
      label: 'Idea',
      sortable: true,
      filterable: true,
      filterConfig: [
        {
          type: 'search',
          key: 'title',
          label: 'Search',
          placeholder: 'Search idea title...'
        }
      ],
      width: 'min-w-[350px]',
      render: (idea) => (
        <div className="space-y-2">
          <div className="font-medium">{idea.title}</div>
          <div className="flex flex-wrap gap-1">
            {idea.tags.slice(0, 3).map((tag) => (
              <Badge key={tag} variant="outline" className="text-xs">
                {tag}
              </Badge>
            ))}
            {idea.tags.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{idea.tags.length - 3}
              </Badge>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      filterable: true,
      filterConfig: [
        {
          type: 'select',
          key: 'status',
          label: 'Status',
          options: [
            { value: 'New', label: 'New' },
            { value: 'Reviewed', label: 'Reviewed' },
            { value: 'In Prioritization', label: 'In Prioritization' },
            { value: 'Selected', label: 'Selected' },
            { value: 'In Development', label: 'In Development' },
            { value: 'Shipped', label: 'Shipped' },
            { value: 'Archived', label: 'Archived' },
            { value: 'Merged', label: 'Merged' }
          ]
        }
      ],
      render: (idea) => (
        <Badge variant="outline">{idea.status}</Badge>
      )
    },
    {
      key: 'priority',
      label: 'Priority',
      sortable: true,
      filterable: true,
      filterConfig: [
        {
          type: 'select',
          key: 'priority',
          label: 'Priority',
          options: [
            { value: '1', label: '1 - Lowest' },
            { value: '2', label: '2 - Low' },
            { value: '3', label: '3 - Medium' },
            { value: '4', label: '4 - High' },
            { value: '5', label: '5 - Critical' }
          ]
        }
      ],
      render: (idea) => getPriorityDot(idea.priority)
    },
    {
      key: 'effort',
      label: 'Effort',
      sortable: true,
      filterable: true,
      defaultVisible: false,
      filterConfig: [
        {
          type: 'select',
          key: 'effort',
          label: 'Effort',
          options: [
            { value: '1', label: '1' },
            { value: '2', label: '2' },
            { value: '3', label: '3' },
            { value: '4', label: '4' },
            { value: '5', label: '5' }
          ]
        }
      ],
      render: (idea) => getScoreDots(idea.effort)
    },
    {
      key: 'impact',
      label: 'Impact',
      sortable: true,
      filterable: true,
      defaultVisible: false,
      filterConfig: [
        {
          type: 'select',
          key: 'impact',
          label: 'Impact',
          options: [
            { value: '1', label: '1' },
            { value: '2', label: '2' },
            { value: '3', label: '3' },
            { value: '4', label: '4' },
            { value: '5', label: '5' }
          ]
        }
      ],
      render: (idea) => getScoreDots(idea.impact)
    },
    {
      key: 'linkedFeedback',
      label: 'Feedback',
      sortable: true,
      filterable: true,
      defaultVisible: false,
      filterConfig: [
        {
          type: 'select',
          key: 'linkedFeedback',
          label: 'Linked Feedback',
          options: [...new Set(ideas.map((i) => String(i.linkedFeedback)))].sort((a, b) => Number(a) - Number(b)).map((v) => ({ value: v, label: v }))
        }
      ],
      render: (idea) => (
        <div className="flex items-center space-x-2">
          <MessageSquare className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm">{idea.linkedFeedback}</span>
        </div>
      )
    },
    {
      key: 'linkedClients',
      label: 'Clients',
      sortable: true,
      filterable: true,
      defaultVisible: false,
      filterConfig: [
        {
          type: 'select',
          key: 'linkedClients',
          label: 'Linked Clients',
          options: [...new Set(ideas.map((i) => String(i.linkedClients)))].sort((a, b) => Number(a) - Number(b)).map((v) => ({ value: v, label: v }))
        }
      ],
      render: (idea) => (
        <div className="flex items-center space-x-2">
          <Building className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm">{idea.linkedClients}</span>
        </div>
      )
    },
    {
      key: 'productAreas',
      label: 'Product Area',
      sortable: false,
      filterable: true,
      defaultVisible: false,
      filterConfig: [
        {
          type: 'search',
          key: 'productAreaNames',
          label: 'Product Area',
          placeholder: 'Search product area...'
        }
      ],
      render: (idea) => {
        if (idea.productAreas && idea.productAreas.length > 0) {
          return (
            <div className="flex items-center space-x-2">
              <Layers className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">{idea.productAreas[0].name}</span>
              {idea.productAreas.length > 1 && (
                <Badge variant="outline" className="text-xs">
                  +{idea.productAreas.length - 1}
                </Badge>
              )}
            </div>
          );
        }
        return <span className="text-sm text-muted-foreground">—</span>;
      }
    },
    {
      key: 'clientValue',
      label: 'Value',
      sortable: true,
      defaultVisible: false,
      render: (idea) => (
        <div className="flex items-center space-x-2">
          <DollarSign className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">
            {formatCurrency(idea.clientValue, regionalSettings)}
          </span>
        </div>
      )
    },
    {
      key: 'jiraStatus',
      label: 'Jira',
      sortable: true,
      defaultVisible: true,
      render: (idea) => {
        const links = idea.jiraLinks ?? [];
        if (links.length > 0) {
          const first = links[0];
          const url = first?.jira_url ?? idea.jiraLink;
          const key = first?.jira_key ?? 'Linked';
          const suffix = links.length > 1 ? ` +${links.length - 1}` : '';
          return (
            <a 
              href={url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center space-x-1 text-sm text-primary hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="h-3 w-3 shrink-0" />
              <span>{key}{suffix}</span>
            </a>
          );
        }
        return <span className="text-sm text-muted-foreground">—</span>;
      }
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      filterable: true,
      filterConfig: [
        {
          type: 'dateRange',
          key: 'createdAt',
          label: 'Date Range'
        }
      ],
      render: (idea) => (
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Calendar className="h-4 w-4" />
          <span>{formatDate(idea.createdAt, regionalSettings)}</span>
        </div>
      )
    },
    {
      key: 'actions',
      label: '',
      width: 'w-[50px]',
      render: (idea) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onViewDetails(idea.id)}>
              <Eye className="h-4 w-4 mr-2" />
              View Details
            </DropdownMenuItem>
            {hasPermission(userRole, 'canUpdateIdeas') && (
              <DropdownMenuItem onClick={() => handleEditIdea(idea)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
            )}
            {hasPermission(userRole, 'canDeleteIdeas') && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  className="text-destructive"
                  onClick={() => handleDeleteIdea(idea.id)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }
  ];

  const bulkActions: BulkAction[] = [
    ...(hasPermission(userRole, 'canUpdateIdeas') ? [{
      key: 'merge',
      label: 'Merge',
      icon: <Merge className="h-4 w-4 mr-2" />,
      variant: 'outline' as const
    },
    {
      key: 'archive',
      label: 'Archive',
      icon: <Archive className="h-4 w-4 mr-2" />,
      variant: 'outline' as const
    },
    {
      key: 'tag',
      label: 'Add Tags',
      icon: <TagIcon className="h-4 w-4 mr-2" />,
      variant: 'outline' as const
    }] : []),
    ...(hasPermission(userRole, 'canDeleteIdeas') ? [{
      key: 'delete',
      label: 'Delete',
      icon: <Trash2 className="h-4 w-4 mr-2" />,
      variant: 'destructive' as const,
      requireConfirmation: true,
      confirmTitle: 'Delete Selected Ideas',
      confirmDescription: 'Are you sure you want to delete the selected ideas? This action cannot be undone.'
    }] : [])
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4 min-w-0">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-lg font-semibold">Idea Management</h1>
          <p className="text-muted-foreground">Track and prioritize product ideas from customer feedback</p>
        </div>
        {hasPermission(userRole, 'canCreateIdeas') && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setImportDialogOpen(true)}>
              <Upload className="h-4 w-4 mr-2" />
              Import
            </Button>
            <Sheet open={dialogOpen} onOpenChange={setDialogOpen}>
              <SheetTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Idea
                </Button>
              </SheetTrigger>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>Create New Idea</SheetTitle>
                <SheetDescription>
                  Add a new product idea to track and prioritize
                </SheetDescription>
              </SheetHeader>
              
              <SheetBody>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Title *</Label>
                    <Input 
                      id="title" 
                      placeholder="Brief descriptive title" 
                      value={newIdea.title}
                      onChange={(e) => setNewIdea({ ...newIdea, title: e.target.value })}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="description">Description *</Label>
                    <Textarea 
                      id="description" 
                      placeholder="Detailed description of the idea..." 
                      rows={4}
                      value={newIdea.description}
                      onChange={(e) => setNewIdea({ ...newIdea, description: e.target.value })}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="status">Status</Label>
                      <Select value={newIdea.status} onValueChange={(value) => setNewIdea({ ...newIdea, status: value })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="New">New</SelectItem>
                          <SelectItem value="Reviewed">Reviewed</SelectItem>
                          <SelectItem value="In Prioritization">In Prioritization</SelectItem>
                          <SelectItem value="Selected">Selected</SelectItem>
                          <SelectItem value="In Development">In Development</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="priority">Priority (1-5)</Label>
                      <Select value={String(newIdea.priority)} onValueChange={(value) => setNewIdea({ ...newIdea, priority: Number(value) as 1 | 2 | 3 | 4 | 5 })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select priority" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 - Lowest</SelectItem>
                          <SelectItem value="2">2 - Low</SelectItem>
                          <SelectItem value="3">3 - Medium</SelectItem>
                          <SelectItem value="4">4 - High</SelectItem>
                          <SelectItem value="5">5 - Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="effort">Effort (1-5)</Label>
                      <Select value={String(newIdea.effort)} onValueChange={(value) => setNewIdea({ ...newIdea, effort: Number(value) as 1 | 2 | 3 | 4 | 5 })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select effort" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 - Minimal</SelectItem>
                          <SelectItem value="2">2 - Low</SelectItem>
                          <SelectItem value="3">3 - Medium</SelectItem>
                          <SelectItem value="4">4 - High</SelectItem>
                          <SelectItem value="5">5 - Very High</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="impact">Impact (1-5)</Label>
                      <Select value={String(newIdea.impact)} onValueChange={(value) => setNewIdea({ ...newIdea, impact: Number(value) as 1 | 2 | 3 | 4 | 5 })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select impact" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 - Minimal</SelectItem>
                          <SelectItem value="2">2 - Low</SelectItem>
                          <SelectItem value="3">3 - Medium</SelectItem>
                          <SelectItem value="4">4 - High</SelectItem>
                          <SelectItem value="5">5 - Very High</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="reach">Reach</Label>
                      <Input
                        id="reach"
                        type="number"
                        min="0"
                        placeholder="Linked clients count used if empty"
                        value={newIdea.reach}
                        onChange={(e) => setNewIdea({ ...newIdea, reach: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confidence">Confidence %</Label>
                      <Select value={newIdea.confidence != null ? String(newIdea.confidence) : ''} onValueChange={(value) => setNewIdea({ ...newIdea, confidence: value ? Number(value) : null })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select confidence" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="50">50%</SelectItem>
                          <SelectItem value="70">70%</SelectItem>
                          <SelectItem value="85">85%</SelectItem>
                          <SelectItem value="100">100%</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="time_criticality">Time Criticality (1-10)</Label>
                      <Select value={newIdea.time_criticality != null ? String(newIdea.time_criticality) : ''} onValueChange={(value) => setNewIdea({ ...newIdea, time_criticality: value ? Number(value) : null })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select urgency" />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                            <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="risk_reduction">Risk Reduction (1-10)</Label>
                      <Select value={newIdea.risk_reduction != null ? String(newIdea.risk_reduction) : ''} onValueChange={(value) => setNewIdea({ ...newIdea, risk_reduction: value ? Number(value) : null })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select risk/opportunity" />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                            <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Product Node Selector */}
                  <ProductNodeSelector
                    selectedNodeIds={selectedProductNodes}
                    onSelectionChange={setSelectedProductNodes}
                  />
                  
                  {/* Client Selector */}
                  <ClientSelector
                    selectedClientIds={selectedClients}
                    onSelectionChange={setSelectedClients}
                  />
                </div>
              </SheetBody>
              
              <SheetFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleCreateIdea}>Create Idea</Button>
              </SheetFooter>
            </SheetContent>
          </Sheet>
          <ImportIdeasDialog
            open={importDialogOpen}
            onOpenChange={setImportDialogOpen}
            onSuccess={loadIdeas}
          />
          </div>
        )}
      </div>

      {/* Advanced Data Table */}
      <AdvancedDataTableV2
        title="Product Ideas"
        description="All product ideas generated from customer feedback"
        data={ideas}
        columns={columns}
        bulkActions={bulkActions}
        pageSize={50}
        onRefresh={handleRefresh}
        onExport={handleExport}
        onBulkAction={handleBulkAction}
        onRowClick={(item) => onViewDetails(item.id)}
        emptyStateTitle="No ideas found"
        emptyStateDescription="Get started by creating your first idea"
        emptyStateIcon={<Lightbulb className="h-8 w-8" />}
        defaultExclude={{ status: ['Archived', 'Merged'] }}
      />

      {/* Edit Dialog */}
      <Sheet open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Edit Idea</SheetTitle>
            <SheetDescription>Update idea details</SheetDescription>
          </SheetHeader>
          
          <SheetBody>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-title">Title *</Label>
                <Input 
                  id="edit-title" 
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-description">Description</Label>
                <Textarea 
                  id="edit-description" 
                  rows={4}
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-status">Status</Label>
                  <Select value={editForm.status} onValueChange={(value) => setEditForm({ ...editForm, status: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="New">New</SelectItem>
                      <SelectItem value="Reviewed">Reviewed</SelectItem>
                      <SelectItem value="In Prioritization">In Prioritization</SelectItem>
                      <SelectItem value="Selected">Selected</SelectItem>
                      <SelectItem value="In Development">In Development</SelectItem>
                      <SelectItem value="Shipped">Shipped</SelectItem>
                      <SelectItem value="Archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-priority">Priority</Label>
                  <Select value={String(editForm.priority)} onValueChange={(value) => setEditForm({ ...editForm, priority: Number(value) as 1 | 2 | 3 | 4 | 5 })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 - Lowest</SelectItem>
                      <SelectItem value="2">2 - Low</SelectItem>
                      <SelectItem value="3">3 - Medium</SelectItem>
                      <SelectItem value="4">4 - High</SelectItem>
                      <SelectItem value="5">5 - Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-effort">Effort Score</Label>
                  <Select value={String(editForm.effort)} onValueChange={(value) => setEditForm({ ...editForm, effort: Number(value) as 1 | 2 | 3 | 4 | 5 })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 - Minimal</SelectItem>
                      <SelectItem value="2">2 - Low</SelectItem>
                      <SelectItem value="3">3 - Medium</SelectItem>
                      <SelectItem value="4">4 - Hard</SelectItem>
                      <SelectItem value="5">5 - Very High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-impact">Impact Score</Label>
                  <Select value={String(editForm.impact)} onValueChange={(value) => setEditForm({ ...editForm, impact: Number(value) as 1 | 2 | 3 | 4 | 5 })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 - Minimal</SelectItem>
                      <SelectItem value="2">2 - Low</SelectItem>
                      <SelectItem value="3">3 - Medium</SelectItem>
                      <SelectItem value="4">4 - High</SelectItem>
                      <SelectItem value="5">5 - Very High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-reach">Reach</Label>
                  <Input
                    id="edit-reach"
                    type="number"
                    min="0"
                    placeholder="Linked clients count used if empty"
                    value={editForm.reach}
                    onChange={(e) => setEditForm({ ...editForm, reach: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-confidence">Confidence %</Label>
                  <Select value={editForm.confidence != null ? String(editForm.confidence) : ''} onValueChange={(value) => setEditForm({ ...editForm, confidence: value ? Number(value) : null })}>
                    <SelectTrigger><SelectValue placeholder="Select confidence" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="50">50%</SelectItem>
                      <SelectItem value="70">70%</SelectItem>
                      <SelectItem value="85">85%</SelectItem>
                      <SelectItem value="100">100%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-time_criticality">Time Criticality (1-10)</Label>
                  <Select value={editForm.time_criticality != null ? String(editForm.time_criticality) : ''} onValueChange={(value) => setEditForm({ ...editForm, time_criticality: value ? Number(value) : null })}>
                    <SelectTrigger><SelectValue placeholder="Select urgency" /></SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                        <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-risk_reduction">Risk Reduction (1-10)</Label>
                  <Select value={editForm.risk_reduction != null ? String(editForm.risk_reduction) : ''} onValueChange={(value) => setEditForm({ ...editForm, risk_reduction: value ? Number(value) : null })}>
                    <SelectTrigger><SelectValue placeholder="Select risk/opportunity" /></SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                        <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Client Selector */}
              <ClientSelector
                selectedClientIds={editSelectedClients}
                onSelectionChange={setEditSelectedClients}
              />
            </div>
          </SheetBody>
          
          <SheetFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateIdea}>Save Changes</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Merge Ideas Dialog */}
      <MergeIdeasDialog
        open={mergeDialogOpen}
        onOpenChange={setMergeDialogOpen}
        ideas={ideas.filter((i) => mergeSelectedIds.includes(i.id))}
        onSuccess={loadIdeas}
      />
    </div>
  );
}